import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Copy, LogOut, Loader2, BellRing, CheckCircle2, ExternalLink } from "lucide-react";
import { API } from "@/lib/site";
import { Logo } from "@/components/landing/Logo";

const KEY = "spotter_admin_token";
const TABS = [["", "All", "all"], ["android-tester", "Android testers", "android-tester"], ["launch", "Launch list", "launch"]];
const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

const Login = ({ onToken }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(KEY, data.token);
      onToken(data.token);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  const field = "h-12 w-full rounded-xl border border-white/15 bg-[#16181f] px-4 text-base focus:outline-none focus:ring-2 focus:ring-[#ff6a3d]/60";
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-24 flex w-full max-w-sm flex-col gap-4 px-5">
      <h1 className="font-display text-5xl font-black uppercase">Owner login</h1>
      <p className="text-sm text-[#b3b8c1]">Private page for your waitlist and tester signups.</p>
      <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className={field} />
      <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className={field} />
      {error && <p data-testid="admin-login-error" className="text-sm text-[#ff8a7a]">{error}</p>}
      <button type="submit" disabled={busy} data-testid="admin-login-submit" className="flex h-12 items-center justify-center rounded-full bg-grad font-semibold text-[#1a0b05] disabled:opacity-70">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
      </button>
    </form>
  );
};

const PlayPrompt = ({ pending, onMarkAll }) => {
  if (!pending.length) {
    return <p data-testid="admin-play-all-done" className="mt-6 flex items-center gap-2 rounded-2xl border border-[#4ade80]/30 bg-[#4ade80]/[0.06] p-4 text-sm text-[#d1fae5]"><CheckCircle2 className="h-4 w-4 text-[#4ade80]" /> All your testers are checked off in the Google Group.</p>;
  }
  const copy = async () => {
    await navigator.clipboard.writeText(pending.join(", "));
    toast.success(`Copied ${pending.length} email${pending.length > 1 ? "s" : ""}`);
  };
  return (
    <div data-testid="admin-play-prompt" className="mt-6 rounded-2xl border border-[#ff6a3d]/40 bg-[#ff6a3d]/[0.07] p-5">
      <p className="flex items-center gap-2 font-semibold"><BellRing className="h-4 w-4 text-[#ff8a3d]" /> {pending.length} tester{pending.length > 1 ? "s" : ""} to check in the Google Group</p>
      <p className="mt-1 text-sm text-[#c4c8cf]">Make sure each one has joined{" "}<a href="https://groups.google.com/g/spotter-app-testers" target="_blank" rel="noopener noreferrer" data-testid="admin-group-inline-link" className="text-[#ff8a3d] underline underline-offset-4">spotter-app-testers</a>, then tick them off here.</p>
      <ul className="mt-3 flex flex-wrap gap-2">
        {pending.map((e) => <li key={e} className="rounded-full bg-[#0e0f12]/70 px-3 py-1 font-mono-x text-xs">{e}</li>)}
      </ul>
      <div className="mt-4 flex flex-wrap gap-2">
        <a href="https://groups.google.com/g/spotter-app-testers" target="_blank" rel="noopener noreferrer" data-testid="admin-open-group" className="inline-flex items-center gap-2 rounded-full bg-grad px-4 py-2 text-sm font-semibold text-[#1a0b05]"><ExternalLink className="h-4 w-4" /> Open the Google Group</a>
        <button onClick={copy} data-testid="admin-play-copy" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-[#ff6a3d]/60"><Copy className="h-4 w-4" /> Copy these emails</button>
        <button onClick={() => onMarkAll(pending)} data-testid="admin-play-mark-all" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-[#4ade80]/60"><CheckCircle2 className="h-4 w-4" /> Mark all as checked</button>
      </div>
    </div>
  );
};

const Row = ({ item, i, onToggle }) => (
  <tr data-testid={`signup-row-${i}`} className="border-t border-white/[0.07]">
    <td className="py-3 pr-4 font-mono-x text-sm">{item.email}</td>
    <td className="py-3 pr-4 text-sm">
      <span className={`rounded-full px-2.5 py-1 text-xs ${item.source === "android-tester" ? "bg-[#ff6a3d]/15 text-[#ff8a3d]" : "bg-white/[0.06] text-[#c4c8cf]"}`}>
        {item.source === "android-tester" ? "Android tester" : "Launch list"}
      </span>
    </td>
    <td className="py-3 pr-4 text-sm text-[#b3b8c1]">{new Date(item.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</td>
    <td className="py-3 text-sm">
      {item.source === "android-tester" ? (
        <label className="inline-flex cursor-pointer items-center gap-2">
          <input type="checkbox" checked={!!item.play_added} onChange={(e) => onToggle([item.email], e.target.checked)} data-testid={`play-added-toggle-${i}`} className="h-4 w-4 accent-[#ff6a3d]" />
          <span className={item.play_added ? "text-[#4ade80]" : "text-[#c4c8cf]"}>{item.play_added ? "In group" : "Not yet"}</span>
        </label>
      ) : <span className="text-[#6b7280]">-</span>}
    </td>
  </tr>
);

const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("android-tester");
  const [data, setData] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/waitlist`, { ...auth, params: tab ? { source: tab } : {} });
      setData(d);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) onLogout();
      else toast.error(errText(err));
    }
  }, [tab, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const items = Array.isArray(data?.items) ? data.items : [];
  const pending = Array.isArray(data?.pending_play) ? data.pending_play : [];
  const markAdded = async (emails, added = true) => {
    try {
      await axios.post(`${API}/admin/testers/play-added`, { emails, added }, auth);
      toast.success(added ? "Checked off in the Google Group" : "Marked as not checked");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };
  const download = async () => {
    const res = await axios.get(`${API}/admin/waitlist.csv`, { ...auth, params: tab ? { source: tab } : {}, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = `spotter-${tab || "all"}-signups.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const copy = async () => {
    await navigator.clipboard.writeText(items.map((i) => i.email).join(", "));
    toast.success(`Copied ${items.length} emails`);
  };
  const btn = "inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm transition-colors hover:border-[#ff6a3d]/60";

  return (
    <div className="mx-auto max-w-5xl px-5 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display text-5xl font-black uppercase sm:text-6xl">Signups</h1>
        <div className="flex flex-wrap gap-2">
          <button onClick={download} data-testid="admin-download-csv" className={`${btn} border-transparent bg-grad font-semibold text-[#1a0b05]`}><Download className="h-4 w-4" /> Download CSV</button>
          <button onClick={copy} data-testid="admin-copy-emails" className={btn}><Copy className="h-4 w-4" /> Copy emails</button>
          <button onClick={onLogout} data-testid="admin-logout" className={btn}><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </div>
      {data !== null && <PlayPrompt pending={pending} onMarkAll={(e) => markAdded(e, true)} />}
      <div className="mt-8 flex flex-wrap gap-2">
        {TABS.map(([k, l, c]) => (
          <button key={l} onClick={() => setTab(k)} data-testid={`admin-tab-${c}`} className={`rounded-full border px-4 py-2 text-sm ${tab === k ? "border-[#ff6a3d] bg-[#ff6a3d]/20" : "border-white/15 text-[#c4c8cf]"}`}>
            {l} <span className="ml-1 font-mono-x text-[#ff8a3d]">{data?.counts?.[c] ?? 0}</span>
          </button>
        ))}
      </div>
      {tab === "android-tester" && <p data-testid="admin-tester-progress" className="mt-4 text-sm text-[#b3b8c1]">{data?.counts?.["android-tester"] ?? 0} of 12 testers needed for the Google Play closed test.</p>}
      <div className="mt-6 overflow-x-auto rounded-2xl border border-white/[0.08] bg-[#16181f] p-5">
        {data === null ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : items.length === 0 ? (
          <p data-testid="admin-empty" className="py-8 text-center text-sm text-[#b3b8c1]">No signups here yet.</p>
        ) : (
          <table className="w-full min-w-[640px] text-left" data-testid="admin-signups-table">
            <thead><tr className="font-mono-x text-xs uppercase tracking-widest text-[#9ca3af]"><th className="pb-3">Email</th><th className="pb-3">Type</th><th className="pb-3">Signed up</th><th className="pb-3">Google Group</th></tr></thead>
            <tbody>{items.map((it, i) => <Row key={it.email} item={it} i={i} onToggle={markAdded} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(KEY));
  const logout = () => { localStorage.removeItem(KEY); setToken(null); };
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);
  return (
    <div className="min-h-screen bg-[#0e0f12] text-[#f2f3f5]">
      <header className="border-b border-white/[0.06]"><div className="mx-auto flex h-16 max-w-5xl items-center px-5"><Logo testId="admin-logo" /></div></header>
      {token ? <Dashboard token={token} onLogout={logout} /> : <Login onToken={setToken} />}
    </div>
  );
}
